/**
 * Highlights specific quotations within a block of text.
 * 
 * This function attempts to find and highlight user-specified quotes by wrapping
 * them with <mark> tags in the source `text` string. It accounts for typographic
 * variations (like curly quotes) and ensures only the first match is replaced.
 * 
 * @param {string} text - The full text content from which the question is derived.
 * @param {string[]} quotations - An array of quotation strings to highlight within the text.
 * @returns {string} - The modified text string with matching quotes highlighted.
 */
export function highlightQuotesInText(text, quotations = []) {
    if (!Array.isArray(quotations) || quotations.length === 0) return text;

    // Normalize curly quotes in the text to standard double quotes
    let highlightedText = text.replace(/[“”]/g, '"');

    quotations.forEach((quote) => {
      if (!quote || quote.length < 10) return; // Ignore short or empty quotes

      const normalizedQuote = quote.replace(/[“”]/g, '"').trim();

      // Escape regex-special characters to prevent misinterpretation
      const escapedQuote = normalizedQuote.replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&"
      );

      // Construct a regex to match the first exact occurrence of the quote
      const regex = new RegExp(escapedQuote, "m");

      // Memoized version of the highlighted text to avoid unnecessary recalculations
      // Re-computes only when either the text or the quotations array changes
      highlightedText = highlightedText.replace(regex, (match) => {
        return `<mark>${match}</mark>`;
      });
    });

    return highlightedText;
  }